import React, { useState, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Transition } from '@headlessui/react'
import { Button, Text, Select, SelectItem } from '@tremor/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function CharacterizationFormModal({ isOpen, onClose, onSubmit, editCharacterization = null, loading = false }) {
    const { t } = useTranslation()
    const isEditing = !!editCharacterization
    const [formData, setFormData] = useState({
        universe: '',
        population: '',
        sample: '',
        sampling_type: '',
        sample_size: '',
        justification: '',
        status: 'draft'
    })
    const [errors, setErrors] = useState({})
    const [submitError, setSubmitError] = useState('')

    useEffect(() => {
        if (editCharacterization) {
            setFormData({
                universe: editCharacterization.universe || '',
                population: editCharacterization.population || '',
                sample: editCharacterization.sample || '',
                sampling_type: editCharacterization.sampling_type || '',
                sample_size: editCharacterization.sample_size || '',
                justification: editCharacterization.justification || '',
                status: editCharacterization.status || 'draft'
            })
        } else {
            // Reset form for new characterization
            setFormData({
                universe: '',
                population: '',
                sample: '',
                sampling_type: '',
                sample_size: '',
                justification: '',
                status: 'draft'
            })
        }
        setErrors({})
        setSubmitError('')
    }, [editCharacterization])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}

        // Universe validation (minimum 20 characters)
        if (!formData.universe?.trim()) {
            newErrors.universe = t('Research.Caracterizacao.Validation.UniverseRequired')
        } else if (formData.universe.trim().length < 20) {
            newErrors.universe = t('Research.Caracterizacao.Validation.UniverseTooShort', { min: 20 })
        }

        // Population validation (minimum 20 characters)
        if (!formData.population?.trim()) {
            newErrors.population = t('Research.Caracterizacao.Validation.PopulationRequired')
        } else if (formData.population.trim().length < 20) {
            newErrors.population = t('Research.Caracterizacao.Validation.PopulationTooShort', { min: 20 })
        }

        // Sample validation (minimum 20 characters)
        if (!formData.sample?.trim()) {
            newErrors.sample = t('Research.Caracterizacao.Validation.SampleRequired')
        } else if (formData.sample.trim().length < 20) {
            newErrors.sample = t('Research.Caracterizacao.Validation.SampleTooShort', { min: 20 })
        }

        // Sampling Type validation
        if (!formData.sampling_type?.trim()) {
            newErrors.sampling_type = t('Research.Caracterizacao.Validation.SamplingTypeRequired')
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }

        try {
            const cleanData = {
                universe: sanitizeUserInput(formData.universe),
                population: sanitizeUserInput(formData.population),
                sample: sanitizeUserInput(formData.sample),
                sampling_type: formData.sampling_type,
                sample_size: formData.sample_size ? sanitizeUserInput(formData.sample_size) : '',
                justification: formData.justification ? sanitizeUserInput(formData.justification) : '',
                status: formData.status,
                type: 'manual'
            }

            await onSubmit(cleanData)
            handleClose()
        } catch (err) {
            console.error('Failed to submit characterization:', err)
            setSubmitError(err.message || t('Research.Caracterizacao.CreateError'))
        }
    }

    const handleClose = () => {
        if (!loading) {
            setFormData({
                universe: '',
                population: '',
                sample: '',
                sampling_type: '',
                sample_size: '',
                justification: '',
                status: 'draft'
            })
            setErrors({})
            setSubmitError('')
            onClose()
        }
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex justify-between items-center mb-6">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        {isEditing 
                                            ? t('Research.Caracterizacao.EditTitle')
                                            : t('Research.Caracterizacao.CreateTitle')
                                        }
                                    </Dialog.Title>
                                    <button
                                        onClick={handleClose}
                                        disabled={loading}
                                        className="rounded-md p-2 hover:bg-gray-100"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {submitError && (
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                            <Text className="text-red-600 text-sm">{submitError}</Text>
                                        </div>
                                    )}

                                    {/* Universe */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.Caracterizacao.Universe')} *
                                        </label>
                                        <div className="relative">
                                            <textarea
                                                rows={3}
                                                value={formData.universe}
                                                onChange={(e) => handleInputChange('universe', e.target.value)}
                                                placeholder={t('Research.Caracterizacao.Concepts.Universe')}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                disabled={loading}
                                            />
                                            <Text className="absolute bottom-2 right-3 text-xs text-gray-400">
                                                {formData.universe.length}
                                            </Text>
                                        </div>
                                        {errors.universe && (
                                            <Text className="text-red-500 text-sm mt-1">
                                                {errors.universe}
                                            </Text>
                                        )}
                                    </div>

                                    {/* Population */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.Caracterizacao.Population')} *
                                        </label>
                                        <div className="relative">
                                            <textarea
                                                rows={3}
                                                value={formData.population}
                                                onChange={(e) => handleInputChange('population', e.target.value)}
                                                placeholder={t('Research.Caracterizacao.Concepts.Population')}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                disabled={loading}
                                            />
                                            <Text className="absolute bottom-2 right-3 text-xs text-gray-400">
                                                {formData.population.length}
                                            </Text>
                                        </div>
                                        {errors.population && (
                                            <Text className="text-red-500 text-sm mt-1">
                                                {errors.population}
                                            </Text>
                                        )}
                                    </div>

                                    {/* Sample */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.Caracterizacao.Sample')} *
                                        </label>
                                        <div className="relative">
                                            <textarea
                                                rows={3}
                                                value={formData.sample}
                                                onChange={(e) => handleInputChange('sample', e.target.value)}
                                                placeholder={t('Research.Caracterizacao.Concepts.Sample')}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                disabled={loading}
                                            />
                                            <Text className="absolute bottom-2 right-3 text-xs text-gray-400">
                                                {formData.sample.length}
                                            </Text>
                                        </div>
                                        {errors.sample && (
                                            <Text className="text-red-500 text-sm mt-1">
                                                {errors.sample}
                                            </Text>
                                        )}
                                    </div>

                                    {/* Sampling Type */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.Caracterizacao.SamplingType')} *
                                        </label>
                                        <Select value={formData.sampling_type} onValueChange={(value) => handleInputChange('sampling_type', value)} disabled={loading}>
                                            <SelectItem value="simple">{t('Research.Caracterizacao.SamplingTypes.Probabilistic.simple')}</SelectItem>
                                            <SelectItem value="stratified">{t('Research.Caracterizacao.SamplingTypes.Probabilistic.stratified')}</SelectItem>
                                            <SelectItem value="systematic">{t('Research.Caracterizacao.SamplingTypes.Probabilistic.systematic')}</SelectItem>
                                            <SelectItem value="cluster">{t('Research.Caracterizacao.SamplingTypes.Probabilistic.cluster')}</SelectItem>
                                            <SelectItem value="convenience">{t('Research.Caracterizacao.SamplingTypes.NonProbabilistic.convenience')}</SelectItem>
                                            <SelectItem value="purposive">{t('Research.Caracterizacao.SamplingTypes.NonProbabilistic.purposive')}</SelectItem>
                                            <SelectItem value="snowball">{t('Research.Caracterizacao.SamplingTypes.NonProbabilistic.snowball')}</SelectItem>
                                            <SelectItem value="quota">{t('Research.Caracterizacao.SamplingTypes.NonProbabilistic.quota')}</SelectItem>
                                        </Select>
                                        {errors.sampling_type && (
                                            <Text className="text-red-500 text-sm mt-1">
                                                {errors.sampling_type}
                                            </Text>
                                        )}
                                    </div>

                                    {/* Sample Size */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.Caracterizacao.SampleSize')} <span className="text-gray-500 text-sm">{t('Research.Caracterizacao.Optional')}</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.sample_size}
                                            onChange={(e) => handleInputChange('sample_size', e.target.value)}
                                            placeholder={t('Research.Caracterizacao.SampleSizePlaceholder')}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            disabled={loading}
                                        />
                                    </div>

                                    {/* Justification */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Research.Caracterizacao.Questions.AdditionalContext')} <span className="text-gray-500 text-sm">{t('Research.Caracterizacao.Optional')}</span>
                                        </label>
                                        <div className="relative">
                                            <textarea
                                                rows={4}
                                                value={formData.justification}
                                                onChange={(e) => handleInputChange('justification', e.target.value)}
                                                placeholder={t('Research.Caracterizacao.Questions.AdditionalContextPlaceholder')}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                disabled={loading}
                                            />
                                            {formData.justification.length > 0 && (
                                                <Text className="absolute bottom-2 right-3 text-xs text-gray-400">
                                                    {formData.justification.length}
                                                </Text>
                                            )}
                                        </div>
                                        <Text className="text-gray-500 text-xs mt-1">
                                            {t('Research.Caracterizacao.Questions.AdditionalContextHelp')}
                                        </Text>
                                    </div>

                                    {/* Status */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('Common.Status')}
                                        </label>
                                        <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)} disabled={loading}>
                                            <SelectItem value="draft">{t('Research.Caracterizacao.Status.draft')}</SelectItem>
                                            <SelectItem value="active">{t('Research.Caracterizacao.Status.active')}</SelectItem>
                                            <SelectItem value="archived">{t('Research.Caracterizacao.Status.archived')}</SelectItem>
                                        </Select>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex justify-end gap-3 pt-4">
                                        <Button
                                            type="button"
                                            onClick={handleClose}
                                            disabled={loading}
                                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                        >
                                            {t('Common.Cancel')}
                                        </Button>
                                        <Button
                                            type="submit"
                                            loading={loading}
                                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                        >
                                            {isEditing ? t('Common.Update') : t('Common.Create')}
                                        </Button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default CharacterizationFormModal